import { Combobox } from "@headlessui/react";
import { Check, ChevronDown } from "lucide-react";
import { useRef, useState } from "react";

// A dropdown that also takes free text. Typing filters the options, and a
// value that isn't listed (a car brand we don't carry yet, say) can be picked
// from the "Use …" row or kept by simply leaving the field.
function CreatableCombobox({
  value,
  onChange,
  options,
  placeholder = "Select or type",
  maxLength,
  inputClassName = "",
}) {
  const [query, setQuery] = useState("");
  // What's typed but not yet picked. Kept in a ref because closing the list
  // clears `query` before the blur that should keep it.
  const pendingRef = useRef("");

  // "lexus" should land on the listed "Lexus", not a lowercase duplicate.
  const findListed = (text) =>
    options.find((option) => option.toLowerCase() === text.toLowerCase());

  const typed = query.trim();
  const filteredOptions = typed
    ? options.filter((option) => option.toLowerCase().includes(typed.toLowerCase()))
    : options;
  const canUseTyped = Boolean(typed) && !findListed(typed);

  const handleChange = (next) => {
    pendingRef.current = "";
    setQuery("");
    onChange(next ?? "");
  };

  // Leaving the field with text typed but nothing picked keeps what was typed.
  const keepTyped = (text) => {
    if (text) handleChange(findListed(text) || text);
  };

  return (
    <Combobox value={value || null} onChange={handleChange} onClose={() => setQuery("")} immediate>
      {({ open }) => (
        // Headless UI's own blur handling skips an onBlur passed to
        // Combobox.Input and, with nothing selected yet, resets the value to
        // null. So read the text on the way down and keep it once that's done.
        <div
          className="relative"
          onBlurCapture={(event) => {
            const leaving = !event.currentTarget.contains(event.relatedTarget);
            if (event.target.tagName !== "INPUT" || !leaving) return;
            const text = pendingRef.current.trim();
            setTimeout(() => keepTyped(text), 0);
          }}
        >
          <Combobox.Input
            className={`w-full rounded-lg border border-[#D0D5DD] bg-white px-4 py-3 pr-10 text-base font-medium text-gray-800 shadow-sm placeholder:text-[#98A2B3] focus:border-[#61013B] focus:outline-none focus:ring-2 focus:ring-[#61013B]/10 ${inputClassName}`}
            displayValue={(selected) => selected || ""}
            onChange={(event) => {
              pendingRef.current = event.target.value;
              setQuery(event.target.value);
            }}
            placeholder={placeholder}
            maxLength={maxLength}
            autoComplete="off"
          />
          <Combobox.Button className="absolute inset-y-0 right-3 flex items-center text-[#667085]">
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </Combobox.Button>

          <Combobox.Options className="absolute left-0 right-0 z-[80] mt-2 max-h-64 overflow-y-auto rounded-xl border border-[#D0D5DD] bg-white py-2 shadow-[0_16px_40px_rgba(16,24,40,0.12)] focus:outline-none empty:invisible">
            {filteredOptions.map((option) => (
              <Combobox.Option
                key={option}
                value={option}
                className={({ focus }) =>
                  `relative cursor-pointer select-none px-4 py-3 text-sm font-medium ${
                    focus ? "bg-[#F9FAFB] text-[#101828]" : "bg-white text-[#344054]"
                  }`
                }
              >
                {({ selected }) => (
                  <>
                    <span className={`block truncate pr-6 ${selected ? "font-semibold" : ""}`}>
                      {option}
                    </span>
                    {selected ? (
                      <span className="absolute inset-y-0 right-4 flex items-center text-[#61013B]">
                        <Check className="h-4 w-4" />
                      </span>
                    ) : null}
                  </>
                )}
              </Combobox.Option>
            ))}
            {/* Last, so Enter on a partial match still picks the listed brand. */}
            {canUseTyped && (
              <Combobox.Option
                value={typed}
                className={({ focus }) =>
                  `cursor-pointer select-none break-words px-4 py-3 text-sm font-medium text-[#61013B] ${
                    focus ? "bg-[#f7eef4]" : "bg-white"
                  }`
                }
              >
                Use “{typed}”
              </Combobox.Option>
            )}
          </Combobox.Options>
        </div>
      )}
    </Combobox>
  );
}

export default CreatableCombobox;
